import { useMemo, useState } from 'react';
import { FeatureRoutes } from './router';
import { Header } from '@/shared/ui/Header';
import { Sidebar } from '@/shared/ui/Sidebar';
import { ServiceProvider, type DIServiceToken } from './di';
import { HttpPatientRepository } from '@/features/patients/infrastructure/patients.service';
import { PATIENTS_SERVICE } from '@/features/patients';
import { AppointmentsService } from '@/features/appointments/infrastructure/appointments.service';
import { APPOINTMENTS_SERVICE } from '@/features/appointments';
import { OrganizationService } from '@/features/org/infrastructure/organization.service';
import { CustomRolesService } from '@/features/org/infrastructure/custom-roles.service';
import { ORG_SERVICE, CUSTOM_ROLES_SERVICE } from '@/features/org';
import { ClinicsService } from '@/features/clinic/infrastructure/clinics.service';
import { UsersService } from '@/features/clinic/infrastructure/users.service';
import { CLINICS_SERVICE, USERS_SERVICE } from '@/features/clinic';
import { ClinicSwitcherDialog } from '@/features/clinic/ui/ClinicSwitcherDialog';
import { SettingsService } from '@/features/settings/infrastructure/settings.service';
import { ProcedureCatalogService } from '@/features/settings/infrastructure/procedure-catalog.service';
import { SETTINGS_SERVICE, PROCEDURE_CATALOG_SERVICE } from '@/features/settings';
import { AuthService } from '@/features/auth/infrastructure/auth.service';
import { AUTH_SERVICE } from '@/features/auth';
import { NoopAnalyticsAdapter, ANALYTICS_PORT } from '@/shared/telemetry';
import { MockRazorpayAdapter } from '@/features/billing/infrastructure/mock-razorpay.adapter';
import { PAYMENT_PORT } from '@/features/billing';
import { PatientTreatmentService, PATIENT_TREATMENT_SERVICE } from '@/features/treatments';
import { InvoiceService, INVOICE_SERVICE } from '@/features/billing';
import { PageEnter } from '@/shared/ui/PageEnter';

const patientsService = new HttpPatientRepository();
const appointmentsService = new AppointmentsService();
const organizationService = new OrganizationService();
const customRolesService = new CustomRolesService();
const clinicsService = new ClinicsService();
const usersService = new UsersService();
const settingsService = new SettingsService();
const procedureCatalogService = new ProcedureCatalogService();
const authService = new AuthService();
const analyticsAdapter = new NoopAnalyticsAdapter();
const paymentAdapter = new MockRazorpayAdapter();
const patientTreatmentService = new PatientTreatmentService();
const invoiceService = new InvoiceService();

const APP_SERVICES: [DIServiceToken<unknown>, unknown][] = [
  [PATIENTS_SERVICE, patientsService],
  [APPOINTMENTS_SERVICE, appointmentsService],
  [ORG_SERVICE, organizationService],
  [CUSTOM_ROLES_SERVICE, customRolesService],
  [CLINICS_SERVICE, clinicsService],
  [USERS_SERVICE, usersService],
  [SETTINGS_SERVICE, settingsService],
  [PROCEDURE_CATALOG_SERVICE, procedureCatalogService],
  [AUTH_SERVICE, authService],
  [ANALYTICS_PORT, analyticsAdapter],
  [PAYMENT_PORT, paymentAdapter],
  [PATIENT_TREATMENT_SERVICE, patientTreatmentService],
  [INVOICE_SERVICE, invoiceService],
];

/**
 * Authenticated shell under `<Route path="/*">`.
 * FeatureRoutes uses relative paths against the splat so leaving patient detail rematches.
 */
export function MainAppLayout(): JSX.Element {
  const [isSidebarCollapsed, setIsSidebarCollapsed] = useState(false);
  const [clinicSwitcherOpen, setClinicSwitcherOpen] = useState(false);
  const services = useMemo(() => APP_SERVICES, []);

  const openClinicSwitcher = () => setClinicSwitcherOpen(true);

  return (
    <div className="flex h-screen overflow-hidden bg-[var(--app-bg)] text-[var(--app-text)] transition-colors duration-300 print:block print:h-auto print:overflow-visible print:bg-white">
      <ServiceProvider services={services}>
        <div className="print:hidden">
          <Sidebar
            isCollapsed={isSidebarCollapsed}
            onToggle={() => setIsSidebarCollapsed((value) => !value)}
            onOpenClinicSwitcher={openClinicSwitcher}
          />
        </div>
        <main className="flex min-h-0 min-w-0 flex-1 flex-col overflow-hidden print:overflow-visible">
          <div className="print:hidden">
            <Header />
          </div>
          <div className="app-canvas min-h-0 flex-1 overflow-y-auto overflow-x-hidden nice-scroll px-4 py-6 lg:px-7 print:overflow-visible print:p-0">
            <PageEnter>
              <FeatureRoutes />
            </PageEnter>
          </div>
        </main>
        <ClinicSwitcherDialog open={clinicSwitcherOpen} onOpenChange={setClinicSwitcherOpen} />
      </ServiceProvider>
    </div>
  );
}
